import type { FieldConfig } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { IssueLinksDefaultValueServer } from '@atlassian/jira-issue-create-commons/src/ui/issue-links-field/index.tsx';
import { transformDefaultIssueLinksValue } from '@atlassian/jira-issue-create-commons/src/ui/issue-links-field/utils.tsx';
import { convertTextToAdf } from '@atlassian/jira-issue-field-textarea/src/ui/edit/utils.tsx';
import {
	DESCRIPTION_TYPE,
	ATTACHMENT_TYPE,
	ISSUE_LINKS_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { CHECKBOX_FIELD, RICH_TEXT_FIELD } from '../../../common/constants/graphql-field-types.tsx';
import type { NonRetainedFieldsForCreateAnotherItem } from './types.tsx';

/*
    FieldIds of these fields are not added in the dirtyFields list even if field value is updated.
    And because of this field value retention doesn't work properly when changing project / issue-type.
*/
export const FIELDS_TO_RETAIN_FIELD_VALUE = [CHECKBOX_FIELD];

export const NON_RETAINED_FIELDS_CREATE_ANOTHER: NonRetainedFieldsForCreateAnotherItem = [
	{
		fieldId: DESCRIPTION_TYPE,
		getClearedDefaultValue: (fieldConfig?: FieldConfig) => {
			if (fieldConfig) {
				const descriptionFieldData = fieldConfig[DESCRIPTION_TYPE];

				return descriptionFieldData.gqlType === RICH_TEXT_FIELD
					? {
							resetRequired: true,
							...convertTextToAdf(''),
						}
					: descriptionFieldData.defaultValue;
			}
			return undefined;
		},
	},
	{
		fieldId: ATTACHMENT_TYPE,
		getClearedDefaultValue: () => ({
			shouldClear: true,
		}),
	},
	{
		fieldId: ISSUE_LINKS_TYPE,
		getClearedDefaultValue: (fieldConfig?: FieldConfig) => {
			if (fieldConfig) {
				const { defaultValue } = fieldConfig[ISSUE_LINKS_TYPE];
				if (Array.isArray(defaultValue)) {
					const issueLinks: IssueLinksDefaultValueServer = [];
					defaultValue.forEach((issueLink) => {
						if (
							issueLink !== undefined &&
							issueLink !== null &&
							(typeof issueLink.id === 'string' || issueLink.id === null) &&
							issueLink.relatedBy !== null &&
							typeof issueLink.relatedBy === 'object' &&
							typeof issueLink.relatedBy.linkTypeName === 'string' &&
							typeof issueLink.relatedBy.relationName === 'string' &&
							typeof issueLink.relatedBy.linkTypeId === 'string' &&
							(issueLink.relatedBy.direction === 'INWARD' ||
								issueLink.relatedBy.direction === 'OUTWARD') &&
							typeof issueLink.issue === 'object' &&
							issueLink.issue !== null &&
							typeof issueLink.issue.avatar === 'object' &&
							issueLink.issue.avatar !== null &&
							typeof issueLink.issue.avatar.small === 'string' &&
							typeof issueLink.issue.avatar.medium === 'string' &&
							typeof issueLink.issue.id === 'string' &&
							typeof issueLink.issue.issueId === 'string' &&
							typeof issueLink.issue.key === 'string'
						) {
							// for type refinement, cannot pass iterable 'issueLink' directly to transformDefaultIssueLinksValue
							const issueLinkExact = {
								id: issueLink.id,
								relatedBy: {
									linkTypeName: issueLink.relatedBy.linkTypeName,
									relationName: issueLink.relatedBy.relationName,
									linkTypeId: issueLink.relatedBy.linkTypeId,
									direction: issueLink.relatedBy.direction,
								},
								issue: {
									avatar: {
										medium: issueLink.issue.avatar.medium,
										small: issueLink.issue.avatar.small,
									},
									id: issueLink.issue.id,
									issueId: issueLink.issue.issueId,
									key: issueLink.issue.key,
								},
							};
							issueLinks.push(issueLinkExact);
						}
					});
					return transformDefaultIssueLinksValue(issueLinks);
				}
			}
			return undefined;
		},
	},
];

export const STATUS_FIELD_SOURCE = 'status-field' as const;
export const ISSUETYPE_FIELD_SOURCE = 'issueType-field' as const;

export type ManageFieldSource = typeof STATUS_FIELD_SOURCE | typeof ISSUETYPE_FIELD_SOURCE;
